import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { SuiClient, getFullnodeUrl } from '@mysten/sui.js/client'

interface WalletContextType {
  address: string | null
  isConnected: boolean
  connect: () => Promise<void>
  disconnect: () => void
  client: SuiClient | null
}

const WalletContext = createContext<WalletContextType | undefined>(undefined)

interface WalletProviderProps {
  children: ReactNode
}

export function WalletProvider({ children }: WalletProviderProps) {
  const [address, setAddress] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [client, setClient] = useState<SuiClient | null>(null)

  useEffect(() => {
    // Initialize Sui client
    const suiClient = new SuiClient({ url: getFullnodeUrl('testnet') })
    setClient(suiClient)

    // Check for existing wallet connection
    const savedAddress = localStorage.getItem('wallet_address')
    if (savedAddress) {
      setAddress(savedAddress)
      setIsConnected(true)
    }
  }, [])

  const connect = async () => {
    try {
      // Check if Sui wallet is available
      if (typeof window !== 'undefined' && window.sui) {
        const accounts = await window.sui.getAccounts()
        if (accounts.length > 0) {
          const selectedAccount = accounts[0]
          setAddress(selectedAccount)
          setIsConnected(true)
          localStorage.setItem('wallet_address', selectedAccount)
        }
      } else {
        // Fallback for development - simulate wallet connection
        const mockAddress = '0x' + Math.random().toString(16).substr(2, 40)
        setAddress(mockAddress)
        setIsConnected(true)
        localStorage.setItem('wallet_address', mockAddress)
        console.log('Mock wallet connected:', mockAddress)
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error)
      throw error
    }
  }

  const disconnect = () => {
    setAddress(null)
    setIsConnected(false)
    localStorage.removeItem('wallet_address')
  }

  const value = {
    address,
    isConnected,
    connect,
    disconnect,
    client,
  }

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  )
}

export function useWallet() {
  const context = useContext(WalletContext)
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider')
  }
  return context
}

// Extend Window interface for Sui wallet
declare global {
  interface Window {
    sui?: {
      getAccounts: () => Promise<string[]>
      requestPermissions: () => Promise<void>
      hasPermissions: () => Promise<boolean>
    }
  }
}
